import axios from 'axios';
import { CourseModel } from '@descomplica/dex-models';
import { ENDPOINTS } from '@/settings/endpoints';
import { showPostgradErrorToast } from '../postgradErrorToast';
import { EVENT_LABELS } from '../analytics/labels';
import { ContractExtensionEligibilityInfo } from '@/models/contractExtension/ContractExtensionEligibilityModel';
import { getChimeraToken } from '../get-tokens';
import SentryClient from '../sentry';
import { PostgradAnalytics } from '../analytics/postgradAnalytics';
import i18n from '@/i18n';

interface GetContractExtensionEligibilityResponseType {
  data: ContractExtensionEligibilityInfo;
}

export default class ContractExtensionRequestService {
  static async getContractExtensionEligibilityInfo({
    course,
    chimeraContractId,
    token,
  }: {
    course: CourseModel;
    chimeraContractId: number;
    token: string;
  }): Promise<ContractExtensionEligibilityInfo | null> {
    try {
      const response = await axios.get<GetContractExtensionEligibilityResponseType>(
        `/eligibilities`,
        {
          baseURL: ENDPOINTS.pegasusMustang.POSTGRAD_CONTRACT_EXTENSION as string,
          params: {
            descomplicaCourseId: course.descomplicaCourseId,
            contractId: chimeraContractId,
          },
          headers: {
            'Authorization-Dex': `Bearer ${token}`,
          },
        },
      );
      return response.data.data;
    } catch (error: any) {
      PostgradAnalytics.trackPegasusError({
        error,
        eventName: EVENT_LABELS.postgradContractExtensionEligibilityError,
        course,
      });

      SentryClient.captureError(error);
    }
    return null;
  }

  static async registerContractExtensionRequest({
    course,
    chimeraToken,
    chimeraContractId,
    period,
  }: {
    course: CourseModel;
    chimeraToken: string;
    chimeraContractId: number;
    period: number;
  }): Promise<boolean> {
    try {
      await axios.post<{ data: boolean }>(
        `/requests`,
        { descomplicaCourseId: course.descomplicaCourseId, contractId: chimeraContractId, period },
        {
          baseURL: ENDPOINTS.pegasusMustang.POSTGRAD_CONTRACT_EXTENSION as string,
          headers: {
            Authorization: `Bearer ${chimeraToken}`,
          },
        },
      );

      return true;
    } catch (error: any) {
      const toastMessage = i18n
        .t('postgradStudyPlanning.expirationDateCard.extendAccess.errorMessage')
        .toString();

      showPostgradErrorToast(error, toastMessage, { longMessage: true });

      PostgradAnalytics.trackPegasusError({
        error,
        eventName: EVENT_LABELS.postgradContractExtensionRequestError,
        course,
      });

      SentryClient.captureError(error);
      return false;
    }
  }

  static async checkAndExtendContracts(): Promise<void> {
    try {
      const response = await axios.put('/extend', null, {
        baseURL: ENDPOINTS.pegasusMustang.POSTGRAD_CONTRACT_EXTENSION as string,
        headers: {
          Authorization: `Bearer ${getChimeraToken()}`,
        },
      });

      if (response.status !== 200) return;

      PostgradAnalytics.trackEvent(EVENT_LABELS.postgradContractExtendedSuccessfully);
    } catch (error: any) {
      PostgradAnalytics.trackPegasusError({
        error,
        eventName: EVENT_LABELS.postgradContractExtensionUpdateCheckError,
      });

      SentryClient.captureError(error);
    }
  }
}
